import {ISubmitTraderFormFieldInput} from 'constants/Api';

import {ITraderFieldLike} from './tradingFormJourneyUtils';

type ValidationContext = {
  entityTypeTitle?: string;
};

type TraderTextInputLike = Partial<ITraderFieldLike> & {
  id: string | number;
  value?: string;
  title?: string;
  validationTypes?: string[];
};

const lshToText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value);
};

const lshToLower = (value?: string | null) =>
  String(value || '')
    .toLowerCase()
    .trim();

const lshValidationList = (field: any): string[] => {
  const validations =
    field?.validationTypes ||
    field?.textInputValidationTypes ||
    field?.textInputValidationType ||
    [];
  return Array.isArray(validations)
    ? validations.map((item) => String(item))
    : [];
};

export const lshGetTraderFormFieldCurrentValue = (field: any): any => {
  if (!field) return undefined;
  return (
    field.value ??
    field.textInputValue ??
    field.singleChoiceOptionValue ??
    field.multiChoiceOptionValues ??
    field.checkBoxValue ??
    field.values
  );
};

export const lshGetTraderFormCustomGroupFields = (
  group: any,
): ITraderFieldLike[] => {
  if (!group) return [];
  return (
    (group.customGroupFields || group.fields || []) as ITraderFieldLike[]
  ).filter(Boolean);
};

export const lshSetTraderFormCustomGroupFields = (
  group: ITraderFieldLike,
  fields: ITraderFieldLike[],
) => {
  if ('customGroupFields' in group) {
    (group as any).customGroupFields = fields;
  }
  if ('fields' in (group as any)) {
    (group as any).fields = fields;
  }
};

export const lshFlattenTraderFormFieldsForEdit = (
  fields: ITraderFieldLike[],
): ITraderFieldLike[] => {
  const flattened: ITraderFieldLike[] = [];
  const queue = [...(fields || [])];
  while (queue.length > 0) {
    const field = queue.shift();
    if (!field) continue;
    flattened.push(field);
    if (String((field as any).__typename || '') === 'CustomGroup') {
      queue.push(
        ...lshGetTraderFormCustomGroupFields(field as ITraderFieldLike),
      );
    }
  }
  return flattened;
};

function lshIsOptionalField(field: any) {
  return (
    Boolean(field?.isOptional) ||
    lshValidationList(field).includes('OPTIONAL_TEXT')
  );
}

function lshIsHiddenField(field: any) {
  return lshValidationList(field).includes('HIDDEN');
}

function lshIsFieldVisible(
  field: ITraderFieldLike,
  allFields: ITraderFieldLike[],
): boolean {
  const dependsOn = (field as any)?.dependsOn;
  if (!dependsOn?.fieldId) return true;

  const parent = allFields.find(
    (item) => String(item?.id) === String(dependsOn.fieldId),
  );
  if (!parent) return true;

  const expectedValues: string[] = (dependsOn.values || []).map((v: any) =>
    String(v),
  );
  if (!expectedValues.length) return true;

  if (
    String((parent as any).__typename || '') === 'TradingFormMultiChoiceOption'
  ) {
    const selected = ((parent as any).multiChoiceOptionValues ||
      (parent as any).values ||
      []) as string[];
    return expectedValues.some((value) =>
      selected.map((item) => String(item)).includes(String(value)),
    );
  }

  const parentValue = lshGetTraderFormFieldCurrentValue(parent);
  if (Array.isArray(parentValue)) {
    const selected = parentValue.map((item) => String(item));
    return expectedValues.some((value) => selected.includes(String(value)));
  }

  return expectedValues.includes(String(parentValue));
}

function lshNormaliseEntityTypeTitle(title?: string): string {
  const value = lshToLower(title).replace(/[\s_-]+/g, ' ');
  if (!value) return 'UNKNOWN';
  if (value.includes('sole')) return 'SOLE_PROPRIETOR';
  if (value.includes('trust')) return 'TRUST';
  if (
    value.includes('co oper') ||
    value.includes('co-oper') ||
    value.includes('cooperative')
  ) {
    return 'CO_OPERATIVE';
  }
  if (value.includes('close')) return 'CLOSE_CORPORATION';
  if (value.includes('public')) return 'PUBLIC_COMPANY';
  if (value.includes('private') || value.includes('pty'))
    return 'PRIVATE_COMPANY';
  return 'UNKNOWN';
}

export const lshValidateTraderTextInputField = (
  field: TraderTextInputLike,
  context?: ValidationContext,
): string | null => {
  const validations = lshValidationList(field);
  const value = String(lshGetTraderFormFieldCurrentValue(field) || '');
  const optional = lshIsOptionalField(field);
  const hidden = lshIsHiddenField(field);

  if (!optional && !hidden && !value) return 'Required';
  if (!value) return null;

  if (validations.includes('ENTITY_REG_NUMBER')) {
    const normalized = String(value || '').replace(/\D/g, '');
    const type = lshNormaliseEntityTypeTitle(context?.entityTypeTitle);
    if (type === 'PRIVATE_COMPANY') {
      if (!/^\d{12}$/.test(normalized) || !normalized.endsWith('07')) {
        return 'Invalid Private Company registration number.';
      }
      return null;
    }
    if (!/^\d{12}$/.test(normalized)) {
      return 'Invalid entity registration number.';
    }
    return null;
  }

  if (validations.includes('ID_NUMBER') && !/^\d{13}$/.test(value)) {
    return 'Invalid ID number';
  }

  if (validations.includes('EMAIL')) {
    const emailRegex =
      /^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/;
    if (!emailRegex.test(value)) return 'Invalid email';
  }

  if (validations.includes('PHONE') && !/^\d{10}$/.test(value)) {
    return 'Invalid phone number';
  }

  if (validations.includes('BANK_ACCOUNT') && !/^\d{8,}$/.test(value)) {
    return 'Invalid bank account number';
  }

  if (validations.includes('POSTAL_CODE') && !/^\d{4}$/.test(value)) {
    return 'Invalid postal code';
  }

  if (validations.includes('NUMBER') && !/^\d+$/.test(value)) {
    return 'Invalid number';
  }

  return null;
};

function lshValidateNonTextField(field: ITraderFieldLike): string | null {
  if (lshIsHiddenField(field)) return null;
  if (lshIsOptionalField(field)) return null;

  if (
    String((field as any).__typename || '') ===
      'TradingFormSingleChoiceOption' ||
    String((field as any).__typename || '') ===
      'TradingFormSingleChoiceOptionCard'
  ) {
    const value =
      (field as any).singleChoiceOptionValue ?? (field as any).value;
    if (value === undefined || value === null || value === '') {
      return 'Required';
    }
  }

  if (String((field as any).__typename || '') === 'TradingFormCheckBox') {
    const value = (field as any).checkBoxValue ?? (field as any).value;
    if (!value) return 'Required';
  }

  if (
    String((field as any).__typename || '') === 'TradingFormMultiChoiceOption'
  ) {
    const values =
      (field as any).multiChoiceOptionValues || (field as any).values || [];
    if (!values.length) return 'Required';
  }

  return null;
}

function lshValidateFieldRecursive(
  field: ITraderFieldLike,
  allFields: ITraderFieldLike[],
  context: ValidationContext,
  errors: Record<string, string>,
) {
  if (!lshIsFieldVisible(field, allFields)) return;

  if (String((field as any).__typename || '') === 'CustomGroup') {
    lshGetTraderFormCustomGroupFields(field as ITraderFieldLike).forEach(
      (child) => lshValidateFieldRecursive(child, allFields, context, errors),
    );
    return;
  }

  const error =
    String((field as any).__typename || '') === 'TradingFormTextInput'
      ? lshValidateTraderTextInputField(field as TraderTextInputLike, context)
      : lshValidateNonTextField(field);

  if (error) {
    errors[String(field.id)] = error;
  }
}

export const lshValidateTraderFormFields = (
  fields: ITraderFieldLike[],
  context: ValidationContext = {},
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const allFields = lshFlattenTraderFormFieldsForEdit(fields);

  fields.forEach((field) =>
    lshValidateFieldRecursive(field, allFields, context, errors),
  );
  return errors;
};

export const lshResolveEntityTypeTitleFromFields = (
  fields: ITraderFieldLike[],
  fallback = '',
): string => {
  const flattened = lshFlattenTraderFormFieldsForEdit(fields);
  const match = flattened.find((field) => {
    if (
      String((field as any).__typename || '') !==
      'TradingFormSingleChoiceOption'
    ) {
      return false;
    }
    const id = String(field?.id || '').toLowerCase();
    const title = String((field as any).title || '').toLowerCase();
    return id.includes('company_type') || title.includes('company type');
  });

  if (!match) return fallback;

  const value = String(
    (match as any).value ?? (match as any).singleChoiceOptionValue ?? '',
  );
  const options = ((match as any).options ||
    (match as any).singleChoiceOptionOptions ||
    []) as Array<{title?: string; value?: string | number}> | [];
  const selected = options.find((option) => String(option?.value) === value);
  return selected?.title || fallback;
};

export const lshGetTraderFieldValidations = lshValidationList;

export const lshMapTraderFormSection = (section: any) => {
  const mapField = (field: any) => {
    switch (String(field?.__typename || '')) {
      case 'TradingFormText':
        return {
          ...field,
          title: field.textTitle,
          body: field.textBody,
          type: field.textType,
        };
      case 'TradingFormTextInput':
        return {
          ...field,
          title: field.textInputTitle,
          value: field.textInputValue,
          validationTypes: field.textInputValidationTypes,
        };
      case 'TradingFormSingleChoiceOption':
        return {
          ...field,
          title: field.singleChoiceOptionTitle,
          value: field.singleChoiceOptionValue,
          options: field.singleChoiceOptionOptions,
          type: field.singleChoiceOptionType,
        };
      case 'TradingFormMultiChoiceOption':
        return {
          ...field,
          title: field.multiChoiceOptionTitle,
          values: (field.multiChoiceOptionValues || []).filter(
            (value: string) =>
              (field.multiChoiceOptionOptions || []).some(
                (option: any) => option.value === value,
              ),
          ),
          options: field.multiChoiceOptionOptions,
        };
      case 'TradingFormCheckBox':
        return {
          ...field,
          title: field.checkBoxTitle,
          value: field.checkBoxValue,
        };
      case 'TradingFormFileUpload':
        return {
          ...field,
          title: field.fileUploadTitle,
          subTitle: field.fileUploadSubTitle,
          isOptional: field.fileUploadIsOptional,
          state: field.fileUploadState,
          value: field.fileUploadValue
            ? field.fileUploadValue.map((item: any) => ({
                ...item,
                name: item?.url?.split('/')?.pop(),
              }))
            : null,
        };
      case 'CustomGroup':
        return {...field};
      default:
        return field;
    }
  };

  const fields = (section?.fields || []) as any[];
  const lshFieldText = (value: unknown) => String(value ?? '');
  const groupedFieldIds = new Set<string>();

  const groupHeaders = fields
    .filter((field) => field?.textType === 'GROUP_HEADER')
    .map((header) => {
      const headerId = lshFieldText(header?.id);
      const headerPrefix = headerId.replace(/_title$/, '');

      const groupedByHeader = fields.filter((field) => {
        const fieldId = lshFieldText(field?.id);
        if (!fieldId || groupedFieldIds.has(fieldId)) return false;
        return lshFieldText(field?.groupBy) === headerId;
      });

      const groupedByPrefix = fields.filter((field) => {
        const fieldId = lshFieldText(field?.id);
        if (!fieldId || fieldId === headerId) return false;
        if (groupedFieldIds.has(fieldId)) return false;
        if (lshFieldText(field?.groupBy) === headerId) return false;
        if (field?.textType === 'GROUP_HEADER') return false;

        return Boolean(headerPrefix) && fieldId.startsWith(`${headerPrefix}_`);
      });

      const groupedFields = [...groupedByHeader, ...groupedByPrefix];
      groupedFields.forEach((field) => {
        groupedFieldIds.add(lshFieldText(field?.id));
      });

      const groupHeader: any = {
        id: header.id,
        title: header.textTitle,
        groupView: header.groupView,
        fields: groupedFields.map((field) => mapField(field)),
      };
      groupHeader.__typename = 'CustomGroup';
      return groupHeader;
    });

  const flattened = fields
    .filter((field) => {
      if (field?.groupBy) return false;
      const fieldId = lshFieldText(field?.id);
      if (!fieldId) return true;
      return !groupedFieldIds.has(fieldId);
    })
    .map((field) => {
      const group = groupHeaders.find(
        (header) => String(header.id || '') === String(field?.id || ''),
      );
      if (group && !(group.fields || []).length) return null;
      return group ?? field;
    });

  return {
    ...section,
    fields: flattened.map((field) => mapField(field)).filter(Boolean),
  };
};

export const lshMapTraderForm = (form: any) => {
  const sections = (form?.sections || []).map((section: any) =>
    lshMapTraderFormSection(section),
  );

  return {
    ...form,
    sections,
  };
};

const lshFieldToSubmission = (
  formItem: ITraderFieldLike,
): ISubmitTraderFormFieldInput | null => {
  const typeName = String((formItem as any).__typename || '');
  switch (typeName) {
    case 'TradingFormText':
      return null;
    case 'TradingFormSingleChoiceOption':
    case 'TradingFormTextInput': {
      const submissionValue =
        (formItem as any).value ??
        (formItem as any).textInputValue ??
        (formItem as any).singleChoiceOptionValue ??
        '';
      return {
        id: String(formItem.id),
        value: lshToText(submissionValue),
        fieldTypeName: typeName,
      };
    }
    case 'TradingFormFileUpload': {
      const files = ((formItem as any).value || []) as any[];
      return {
        id: String(formItem.id),
        value: JSON.stringify(files.map((item) => item.url || item.uri)),
        fieldTypeName: typeName,
      };
    }
    case 'TradingFormCheckBox': {
      return {
        id: String(formItem.id),
        value: String(Boolean((formItem as any).value)),
        fieldTypeName: typeName,
      };
    }
    case 'TradingFormMultiChoiceOption': {
      const selected =
        (formItem as any).multiChoiceOptionValues ||
        (formItem as any).values ||
        [];
      return {
        id: String(formItem.id),
        value:
          selected.length > 0 ? JSON.stringify(selected) : (undefined as any),
        fieldTypeName: typeName,
      };
    }
    default:
      return null;
  }
};

const lshFlattenFieldsForSubmission = (
  fields: ITraderFieldLike[],
): ITraderFieldLike[] =>
  (fields || []).flatMap((field) => {
    if (!field) return [];
    if (String((field as any).__typename || '') !== 'CustomGroup')
      return [field];

    const groupFields =
      (lshGetTraderFormCustomGroupFields(
        field as ITraderFieldLike,
      ) as ITraderFieldLike[]) || [];

    return lshFlattenFieldsForSubmission(groupFields);
  });

export const lshFilterFieldDependants = (
  field: ITraderFieldLike,
  formFields: ITraderFieldLike[],
) => {
  const dependsOnField = formFields.find(
    (formItem) =>
      String(formItem.id) === String((field as any)?.dependsOn?.fieldId),
  );

  if (!dependsOnField) return true;

  if (
    String((dependsOnField as any).__typename || '') ===
    'TradingFormMultiChoiceOption'
  ) {
    const selected =
      (dependsOnField as any).multiChoiceOptionValues ||
      (dependsOnField as any).values ||
      [];
    return ((field as any)?.dependsOn?.values || []).some((value: string) =>
      selected.includes(value),
    );
  }

  const dependsOnValue =
    (dependsOnField as any).value ??
    (dependsOnField as any).textInputValue ??
    (dependsOnField as any).singleChoiceOptionValue ??
    (dependsOnField as any).checkBoxValue ??
    null;

  return ((field as any)?.dependsOn?.values || []).includes(dependsOnValue);
};

export const lshPrepareTraderFieldSubmission = (
  formFields: ITraderFieldLike[],
): ISubmitTraderFormFieldInput[] => {
  const flattenedFields = lshFlattenFieldsForSubmission(formFields);

  const submissions = flattenedFields
    .filter((field) => lshFilterFieldDependants(field, flattenedFields))
    .map((formItem) => lshFieldToSubmission(formItem))
    .flatMap((item) => item || []);

  return submissions.reduce<ISubmitTraderFormFieldInput[]>((acc, item) => {
    if (!item) return acc;
    if (
      item.value === undefined ||
      item.value === null ||
      String(item.value) === ''
    ) {
      return acc;
    }
    acc.push(item);
    return acc;
  }, []);
};
